import { Link } from '@tanstack/react-router';
import type { AnchorHTMLAttributes } from 'react';
import { useId } from 'react';

import { cn } from '#/utils/cn';

import type { IconId } from './icons';
import { Icon } from './icons';

interface CategoryCardProps extends Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel' | 'title'
> {
  eyebrow: string;
  title: string;
  description?: string | null;
  href?: string;
  to?: string;
  icon?: IconId;
  iconSize?: number;
  className?: string;
  /** GTM `data-track` id, forwarded onto the card anchor. */
  dataTrack?: string;
  /** GTM `data-track-label`, forwarded onto the card anchor. */
  dataTrackLabel?: string;
  /** GTM `data-track-context`, forwarded onto the card anchor. */
  dataTrackContext?: string;
}

export function CategoryCard({
  eyebrow,
  title,
  description,
  href,
  to,
  icon = 'folder',
  iconSize = 18,
  className,
  target,
  rel,
  dataTrack,
  dataTrackLabel,
  dataTrackContext,
}: CategoryCardProps) {
  const titleId = useId();
  const cardClasses = cn(
    'border-blue-20 bg-gray-10 group/category-card focus-visible:outline-blue-60 flex h-full min-h-160 w-full flex-col rounded border p-24 text-left no-underline shadow-(--shadow-card) transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
    (href || to) && 'hover:border-blue-30 cursor-pointer',
    className,
  );

  const content = (
    <>
      <div className="flex items-start justify-between gap-16">
        <p className="text-static-12 text-fg-strong leading-none font-semibold">
          {eyebrow}
        </p>

        <span className="bg-steel-30 -mt-8 -mr-8 flex size-32 shrink-0 items-center justify-center rounded-full text-slate-50">
          <Icon id={icon} size={iconSize} aria-hidden="true" />
        </span>
      </div>

      <div className="flow flow-4 mt-auto min-w-0">
        <h3
          id={titleId}
          className="text-fg-strong text-fluid-body-lg leading-tight font-normal"
        >
          {title}
        </h3>

        {description ? (
          <p
            className="text-static-12 text-fg-muted line-clamp-4 leading-relaxed"
            title={description}
          >
            {description}
          </p>
        ) : null}
      </div>
    </>
  );

  if (!href && !to) {
    return <article className={cardClasses}>{content}</article>;
  }

  if (to) {
    return (
      <Link
        to={to}
        aria-labelledby={titleId}
        className={cardClasses}
        data-track={dataTrack}
        data-track-label={dataTrackLabel}
        data-track-context={dataTrackContext}
      >
        {content}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      aria-labelledby={titleId}
      className={cardClasses}
      data-track={dataTrack}
      data-track-label={dataTrackLabel}
      data-track-context={dataTrackContext}
    >
      {content}
    </a>
  );
}
